import type { BlockAbstract, PageAbstract, PageItem, PageStackTree, PageStateWithValue, SelectedMode } from '@lighthouse/core'
import { BlockType, PAGE_STACK_DISPLAY_TYPE } from '@lighthouse/core'
import {
    blockHasSomeChildren,
    deepFillSyncBlock,
    fillSyncBlock,
    findBlockById,
    floatPageTree,
    getBlockChildren,
    transformOriginBlock
} from '@lighthouse/shared'
import { arrayMove } from '@lighthouse/tools'
import type { WritableDraft } from 'immer/dist/internal'
import { atom } from 'jotai'
import { clone, filter, omit, reduce } from 'rambda'

import * as srv from '@/services'

import { setLanguageAtom } from '../application/action'
import { currentBreakPointIdAtom, languageAtom, mainBreakPointAtom, pageStackOfBlockAtom, syncComponentsAtom } from '../application/state'
import { applyDraftPayload } from '../utils/applyDraftPayload'
import { hasBlockNegativeUpdate, isViewBlock } from '../utils/block'
import { equalPageStack } from '../utils/equalPageStack'
import { findByPageByTree, floatListToTree, getPrevPageByTree, pageTreeAddLevel, pageTreeToFilterPageList } from '../utils/page'
import {
    blocksAtom,
    containerRuntimeStateAtomFamily,
    homePageAtom,
    languagePageListAtom,
    lastPageOfStackAtom,
    pageAtomFamily,
    pageInternalStateAtomFamily,
    pageStackAtom,
    pageStackBreadcrumbAtomFamily,
    pageStackHistoriesAtom,
    pageStateAtomFamily,
    pageTreeListAtom,
    viewRuntimeStateAtomFamily
} from './state'
import type { FetchBlocksAtomPayload, MovePagePayload, NodeIdWithScope, PageInternalState } from './types'
import {
    AsideType,
    type CreateBlockAtomPayload,
    type CreatePagePayload,
    type RemoveBlockAtomPayload,
    type UpdateBlockAtomPayload,
    type UpdateLocalBlockAtom
} from './types'
import { getNode, stackFactory } from './utils'

/**
 * 获取页面列表
 */
export const fetchPageListAtom = atom(null, async (get, set) => {
    const data = await srv.getPages()
    const tree = pageTreeAddLevel(data)
    set(pageTreeListAtom, tree)
})

/**
 * 获取页面信息
 */
export const fetchPageAtom = atom(null, async (_, set, pageId: string) => {
    const res = await srv.getPage({ params: { id: pageId } })
    if (!res) {
        return
    }

    const pageAtom = pageAtomFamily(pageId)
    set(pageAtom, draft => {
        applyDraftPayload(draft, res)
    })
    return res
})

export const fetchBlocksAtom = atom(null, async (get, set, { pageId, stackId }: FetchBlocksAtomPayload) => {
    const blocks = await srv.getBlocks({ params: { id: pageId } })
    if (!blocks) {
        return
    }

    set(blocksAtom, draft => void (draft[pageId] = blocks))

    const breakPointId = get(currentBreakPointIdAtom)
    const mainBreakPoint = get(mainBreakPointAtom)
    const transformBreakpointBlocks = transformOriginBlock(blocks, breakPointId, mainBreakPoint)
    const syncComponents = get(syncComponentsAtom)
    set(initContainerRuntimeStateAtom, { stackId, blocks: transformBreakpointBlocks, syncComponents })
    return blocks
})

/** 创建页面 */
export const createPageAtom = atom(null, async (get, set, payload: CreatePagePayload) => {
    const lang = get(languageAtom)
    const pageId = await srv.addPage({ ...payload, lang: payload?.lang ?? lang })
    await set(fetchPageListAtom)
    return pageId
})

export const clonePageAtom = atom(null, async (_, set, payload: { lang: string; id: string; groupId?: string }) => {
    const { lang, id, groupId } = payload
    const pageId = await srv.clonePage(id, lang, groupId)
    await set(fetchPageListAtom)
    return pageId
})

export const updateLocalPageAtom = atom(null, (_, set, payload: Partial<PageAbstract> & { id: string }) => {
    set(pageTreeListAtom, draft => {
        const page = findByPageByTree(draft)(payload.id)
        if (!page) {
            return
        }

        for (const [k, v] of Object.entries(payload)) {
            Reflect.set(page, k, v)
        }
    })
})
export const updateRemotePageAtom = atom(null, (_, set, payload: Partial<PageAbstract> & { id: string }) => {
    return srv.updatePage(payload)
})

/** 更新页面 */
export const updatePageAtom = atom(null, (_, set, payload: Partial<PageAbstract> & { id: string }) => {
    set(pageTreeListAtom, draft => {
        const page = findByPageByTree(draft)(payload.id)
        if (!page) {
            return
        }

        for (const [k, v] of Object.entries(payload)) {
            Reflect.set(page, k, v)
        }
    })

    set(pageTreeListAtom, draft => {
        const page = findByPageByTree(draft)(payload.id)
        if (!page) {
            return draft
        }
        for (const [k, v] of Object.entries(payload)) {
            Reflect.set(page, k, v)
        }
    })
    return srv.updatePage(payload)
})

/** 更新所有页面配置 */
export const updatePageListAtom = atom(null, (get, set, payload: { isShowNavbar?: boolean; isShowTabBar?: boolean }) => {
    set(pageTreeListAtom, draft => {
        const list = pageTreeToFilterPageList(draft, [])
        for (const page of list) {
            applyDraftPayload(page, payload)
        }
    })
    return srv.updatePageNavbar(payload)
})

/** 设置页面主页 */
export const updateHomePageAtom = atom(null, async (get, set, payload: Pick<PageAbstract, 'id' | 'isHome'>) => {
    const isSuccess = await srv.updatePage(payload)
    if (isSuccess) {
        set(pageTreeListAtom, draft => {
            const page = findByPageByTree(draft)(payload.id)
            const homePage = findByPageByTree(draft)(get(homePageAtom)?.id ?? '')
            if (!page) {
                return
            }
            if (homePage && homePage.type === 'COMMON') {
                homePage.isHome = false
            }
            for (const [k, v] of Object.entries(payload)) {
                Reflect.set(page, k, v)
            }
        })
    }
    return isSuccess
})

/** 移除页面 */
export const removePageAtom = atom(null, async (get, set, id: string) => {
    const pageItem = findByPageByTree(get(pageTreeListAtom))(id)
    if (!pageItem) {
        return
    }

    const currentPageId = get(lastPageOfStackAtom)?.pageId
    const isRemoveRoot = get(pageStackAtom)?.pageId === id

    const removedStackIds: string[] = []

    const homePage = get(homePageAtom)
    const newStack = homePage && stackFactory({ pageId: homePage.id })

    // 清除stack page tree
    set(pageStackAtom, draft => {
        if (!draft) {
            return
        }

        // 如果删除的是根节点，则清空并设置新的根节点
        if (isRemoveRoot) {
            return newStack ?? null
        }

        function remove(tree: WritableDraft<PageStackTree>, removePageId: string) {
            if (!tree.children) {
                return
            }
            for (let index = 0; index < tree.children.length; index++) {
                const stack = tree.children[index]
                if (stack.pageId === removePageId) {
                    removedStackIds.push(stack.stackId)
                    tree.children.splice(index, 1, ...(stack.children || []))
                    index--
                    continue
                }

                remove(stack, removePageId)
            }
        }

        remove(draft, id)
    })

    // 清除被删除的根页面的访问记录
    set(pageStackHistoriesAtom, draft => {
        if (isRemoveRoot) {
            if (newStack) {
                return [newStack.stackId]
            }
            return []
        }

        return draft.filter(item => !removedStackIds.includes(item))
    })

    // 如果删除了当前页面，则跳转到主页
    const isRemoveSelf =
        !!currentPageId &&
        ((pageItem.type === 'GROUP' && !!findByPageByTree(pageItem.children)(currentPageId)) ||
            (pageItem.type === 'COMMON' && id === currentPageId))

    if (newStack && isRemoveSelf && !isRemoveRoot) {
        const currentPageStackStackId = get(pageStackHistoriesAtom).at(-1)
        if (!currentPageStackStackId) {
            return
        }
        set(pageStackAtom, draft => {
            const stack = equalPageStack(currentPageStackStackId)(draft)
            if (stack) {
                if (stack.children) {
                    stack.children.push(newStack)
                } else {
                    stack.children = [newStack]
                }
            }
        })
        set(pageStackHistoriesAtom, draft => {
            draft.push(newStack.stackId)
        })
    }

    const isSuccess = await srv.deletePage(id)
    if (isSuccess) {
        await set(fetchPageListAtom)
    }
    return homePage
})

/** 移动页面 */
export const movePageAtom = atom(null, (get, set, payload: MovePagePayload) => {
    const { id, oldId, newId, position } = payload
    const trees = get(pageTreeListAtom)
    const floatTrees = floatPageTree(trees, [])
    let list = clone(floatTrees)
    let active: null | PageItem = null
    if (oldId === newId) {
        const oldIndex = floatTrees.findIndex(item => item.id === oldId)
        active = list[oldIndex]
        if (position === 'next') {
            const prevItem = floatTrees?.[oldIndex - 1]
            active.parentId = prevItem.parentId
        }
        if (position === 'prev') {
            const prevItem = floatTrees?.[oldIndex - 1]
            active.parentId = prevItem.type === 'GROUP' ? prevItem.parentId : undefined
        }
        if (position === 'current') {
            const prevItem = floatTrees?.[oldIndex - 1]
            active.parentId = prevItem.type === 'GROUP' ? prevItem.id : undefined
        }
    } else {
        const [oldIndex, newIndex] = reduce<PageItem, number[]>(
            (preVal, curVal, i: number) => {
                if (curVal.id === oldId) {
                    preVal[0] = i
                    return preVal
                }
                if (curVal.id === newId) {
                    preVal[1] = i
                    return preVal
                }
                return preVal
            },
            [-1, -1],
            floatTrees
        )
        const offsetIndex = oldIndex >= newIndex ? -1 : 0
        const prevItem = floatTrees[newIndex + offsetIndex]?.id === newId ? floatTrees[newIndex] : floatTrees[newIndex - 1]
        list = clone(arrayMove(floatTrees ?? [], oldIndex, newIndex))
        active = list[newIndex]
        if (position === 'next') {
            active.parentId = prevItem.parentId
        }
        if (position === 'prev') {
            active.parentId = prevItem?.type === 'GROUP' ? prevItem?.parentId : undefined
        }
        if (position === 'current') {
            active.parentId = prevItem?.type === 'GROUP' ? prevItem?.id : prevItem?.parentId
        }
    }
    const newTree = floatListToTree(list)
    set(pageTreeListAtom, newTree)
    const after = getPrevPageByTree(newTree, active.id)?.id
    return srv.movePage({ id: active.id, after, groupId: active.parentId })
})

/** 初始页面栈信息 */
export const initPageStackAtom = atom(null, (get, set) => {
    const pageList = get(languagePageListAtom)
    const page = pageList[0]
    if (!page) {
        return
    }

    const pageStack = stackFactory({ pageId: page.id })
    set(pageStackAtom, pageStack)
    set(pageStackHistoriesAtom, [pageStack.stackId])
})

export const openPageStackAtom = atom(null, (get, set, payload: PageStackTree) => {
    const stackTree = get(pageStackAtom)
    const stackHistories = get(pageStackHistoriesAtom)

    if (stackHistories.length === 0 && !stackTree) {
        set(pageStackAtom, payload)

        set(pageStackHistoriesAtom, draft => {
            draft.push(payload.stackId)
        })
        return
    }

    const stackId = stackHistories[stackHistories.length - 1]

    set(pageStackAtom, draft => {
        if (!draft) {
            return
        }
        const currentNode = getNode(stackId)(draft)
        if (!currentNode) {
            return
        }

        if (currentNode.children) {
            currentNode.children.push(payload)
        } else {
            currentNode.children = [payload]
        }
    })

    set(pageStackHistoriesAtom, draft => {
        draft.push(payload.stackId)
    })
})

/** 关闭当前以 layer 形式呈现的栈 */
export const closeCurrentPageLayerStackAtom = atom(null, (get, set) => {
    const indexes = get(pageStackHistoriesAtom)
    const stackId = indexes[indexes.length - 1]
    const pageStackBreadcrumb = get(pageStackBreadcrumbAtomFamily(stackId))

    set(pageStackHistoriesAtom, draft => {
        const current = draft[draft.length - 1]
        const prev = pageStackBreadcrumb[pageStackBreadcrumb.length - 2]

        const pageStack = get(pageStackAtom)

        if (current && pageStack) {
            draft.push(prev ? prev.stackId : pageStack.stackId)
        }
    })
})

/** 关闭所有弹窗形式的栈 */
export const closeAllPageLayerAtom = atom(null, (get, set) => {
    const stackTree = get(pageStackAtom)
    if (!stackTree) {
        return
    }
    set(pageStackHistoriesAtom, draft => {
        const stackId = draft[draft.length - 1]

        let recentPageTypeParentStack: PageStackTree | undefined
        function getParentPageTypeStack(tree: PageStackTree): boolean {
            if (!tree.children) {
                return false
            }

            for (const node of tree.children) {
                if (node.stackId === stackId) {
                    return true
                }

                const res = getParentPageTypeStack(node)
                if (res) {
                    if (node.pageStackDisplayType === PAGE_STACK_DISPLAY_TYPE.page && !recentPageTypeParentStack) {
                        recentPageTypeParentStack = omit('children', node)
                    }
                    return res
                }
            }

            return false
        }

        getParentPageTypeStack(stackTree)

        draft.push(recentPageTypeParentStack ? recentPageTypeParentStack.stackId : stackTree.stackId)
    })
})

/** 回退页面栈历史记录 */
export const rollbackPageStackHistoryAtom = atom(null, (get, set) => {
    set(pageStackHistoriesAtom, draft => {
        if (draft.length === 1) {
            return
        }
        draft.pop()
    })
    const stack = get(lastPageOfStackAtom)
    const page = stack && get(pageAtomFamily(stack?.pageId))
    stack?.pageStackDisplayType === 'page' && page && set(setLanguageAtom, page.language)
})

/** 创建页面block */
export const createBlockAtom = atom(null, async (get, set, payload: CreateBlockAtomPayload) => {
    const { createdBlocks, blocks, pageId, stackId, autoSelect = true, scope, skipInspection } = payload

    const syncComponents = get(syncComponentsAtom)
    const filledBlocks = deepFillSyncBlock(createdBlocks, syncComponents)

    // 初始化容器运行时状态
    set(initContainerRuntimeStateAtom, { stackId, blocks: createdBlocks, syncComponents })

    // 设置选中状态
    if (autoSelect) {
        set(pageInternalStateAtomFamily(stackId), draft => {
            draft.asideType = AsideType.BLOCK
            draft.selectedNodes = [{ id: createdBlocks[0]?.id, scope }]
        })
    }

    const res = await srv.createBlocks({
        addedBlocks: createdBlocks,
        pageId,
        allBlocks: blocks,
        skipInspection
    })

    if (!res) {
        return false
    }

    // 创建视图block时，需要获取视图的详情和表单页面
    if (filledBlocks.some(item => blockHasSomeChildren(isViewBlock)(item))) {
        await set(fetchPageListAtom)
    }
    return res
})

export const updateRemoteBlockAtom = atom(null, async (_, set, payload: UpdateBlockAtomPayload) => {
    const { pageId, stackId, block, origin, appId, blocks } = payload
    const res = await srv.updateBlock({ updatedBlocks: [block], allBlocks: blocks, pageId })

    if (res && hasBlockNegativeUpdate(appId, block, origin)) {
        await set(fetchBlocksAtom, { pageId, stackId })
    }
})
export const updateLocalBlockAtom = atom(null, (_, set, payload: UpdateLocalBlockAtom) => {
    const { pageId, block } = payload
    set(blocksAtom, draft => {
        const currentBlocks = draft[pageId]
        if (!currentBlocks) {
            return
        }
        const draftBlock = findBlockById(block.id, currentBlocks)
        if (!draftBlock) {
            return
        }
        Object.entries(block).forEach(([k, v]) => {
            Reflect.set(draftBlock, k, v)
        })
    })
})

/** 移除页面block */
export const removeBlockAtom = atom(null, (_, set, payload: RemoveBlockAtomPayload) => {
    const { stackId, pageId, blocks, removedBlocks, clearSelected = true } = payload

    if (clearSelected) {
        set(clearSelectionAtom, stackId)
    }

    return srv.deleteBlock({ removedBlocks, blocks, pageId })
})

export const addPageRolesAtom = atom(null, async (_, set, params: { roleIds: string[]; departmentIds: string[]; pageId: string }) => {
    const { pageId, roleIds, departmentIds } = params
    const isSuccess = await srv.addRoleToPage(pageId, roleIds, departmentIds)
    if (isSuccess) {
        set(pageTreeListAtom, draft => {
            const page = findByPageByTree(draft)(pageId)
            if (page && page.type === 'COMMON') {
                page.roleIds = roleIds
                page.departmentIds = departmentIds
            }
            return draft
        })
    }
})

export const removePageRolesAtom = atom(null, (_, set, params: { id: string; pageId: string; type: 'role' | 'department' }) => {
    const { pageId, id, type } = params
    set(pageTreeListAtom, draft => {
        const page = findByPageByTree(draft)(pageId)
        if (page && page.type === 'COMMON') {
            const { roleIds = [], departmentIds } = page
            if (type === 'role') {
                page.roleIds = filter(roleId => roleId !== id, roleIds)
            } else {
                page.departmentIds = filter(departmentId => departmentId !== id, departmentIds)
            }
        }
        return draft
    })
    return srv.deleteRole(pageId, id, type)
})

// ============== PageInternalState Actions ==============

/**
 * 设置页面内部状态
 */
export const setPageInternalStateAtom = atom(null, (get, set, payload: { stackId: string; state: Partial<PageInternalState> }) => {
    const { stackId, state } = payload
    set(pageInternalStateAtomFamily(stackId), draft => {
        Object.assign(draft, state)
    })
})

/**
 * 设置选中的节点
 */
export const setSelectedNodesAtom = atom(null, (get, set, payload: { stackId: string; selectedNodes?: NodeIdWithScope[] }) => {
    const { stackId, selectedNodes } = payload
    set(pageInternalStateAtomFamily(stackId), draft => {
        draft.selectedNodes = selectedNodes
    })
})

/**
 * 设置侧边栏类型
 */
export const setAsideTypeAtom = atom(null, (get, set, payload: { stackId: string; asideType: AsideType }) => {
    const { stackId, asideType } = payload
    set(pageInternalStateAtomFamily(stackId), draft => {
        draft.asideType = asideType
    })
})

/**
 * 清除选中状态并设置侧边栏为页面模式
 */
export const clearSelectionAtom = atom(null, (get, set, stackId: string) => {
    set(pageInternalStateAtomFamily(stackId), draft => {
        draft.selectedNodes = undefined
        draft.asideType = AsideType.PAGE
    })
})

// ============== Runtime State Actions ==============

interface InitContainerRuntimeStateOption {
    stackId: string
    blocks: BlockAbstract[]
    syncComponents: BlockAbstract[]
    isReplaceExist?: boolean
}

/**
 * 初始化容器运行时状态
 * 遍历 blocks 找出所有 container，为其创建独立的运行时状态
 */
export const initContainerRuntimeStateAtom = atom(null, (get, set, option: InitContainerRuntimeStateOption) => {
    const { stackId, blocks, syncComponents, isReplaceExist } = option

    function deepInit(block: BlockAbstract, _scope?: string) {
        const filledBlock = fillSyncBlock(block, syncComponents)
        const newScope = _scope || (filledBlock.isMasterSynchronous ? filledBlock.id : undefined)
        const containerId = _scope ? `${_scope}@${filledBlock.id}` : filledBlock.id

        if (filledBlock.type === BlockType.container) {
            const key = { stackId, containerId }
            const existingState = get(containerRuntimeStateAtomFamily(key))

            // 如果已经有对应的值则跳过（除非明确要求替换）
            if (
                existingState?.currentView &&
                filledBlock.config.viewList.some(item => item.id === existingState.currentView) &&
                !isReplaceExist
            ) {
                // 继续处理子节点
            } else {
                const defaultView = filledBlock.config.breakPoint.panel?.defaultPanel || filledBlock.config.viewList?.[0]?.id
                set(containerRuntimeStateAtomFamily(key), {
                    currentView: defaultView,
                    recordStates: existingState?.recordStates
                })
            }
        }

        // 递归处理子节点
        const children = getBlockChildren(filledBlock)
        if (children && children.length !== 0) {
            children.forEach(child => {
                deepInit(child, newScope)
            })
        }
    }

    blocks.forEach(block => {
        deepInit(block)
    })
})

/**
 * 设置容器运行时状态
 */
export const setContainerRuntimeStateAtom = atom(
    null,
    (
        get,
        set,
        payload: {
            stackId: string
            containerId: string
            currentView?: string
            recordId?: string
            /** 同时更新全局状态（当有 recordId 时） */
            updateGlobal?: boolean
            /** 更新所有已存在记录的状态 */
            updateAllRecords?: boolean
        }
    ) => {
        const { stackId, containerId, currentView, recordId, updateGlobal, updateAllRecords } = payload
        const key = { stackId, containerId }

        set(containerRuntimeStateAtomFamily(key), draft => {
            if (!draft) {
                draft = { currentView: currentView || '' }
            }

            if (updateAllRecords && currentView) {
                // 更新全局状态和所有已存在记录的状态
                draft.currentView = currentView
                if (draft.recordStates) {
                    Object.keys(draft.recordStates).forEach(recId => {
                        if (draft?.recordStates) {
                            draft.recordStates[recId] = { currentView }
                        }
                    })
                }
            } else if (recordId) {
                // 更新 recordId 隔离的状态
                if (!draft.recordStates) {
                    draft.recordStates = {}
                }
                if (currentView) {
                    draft.recordStates[recordId] = { currentView }
                    // 如果 updateGlobal 为 true，同时更新全局状态
                    if (updateGlobal) {
                        draft.currentView = currentView
                    }
                }
            } else if (currentView) {
                // 更新全局状态
                draft.currentView = currentView
            }

            return draft
        })
    }
)

/**
 * 设置视图选择状态
 */
export const setViewRuntimeStateAtom = atom(
    null,
    (
        get,
        set,
        payload: {
            stackId: string
            viewId: string
            scope?: string
            selectedIds: string[]
            selectedMode?: SelectedMode
        }
    ) => {
        const { stackId, ...viewState } = payload
        set(viewRuntimeStateAtomFamily(stackId), viewState)
    }
)

/**
 * 清除视图选择状态
 */
export const clearViewRuntimeStateAtom = atom(null, (get, set, stackId: string) => {
    set(viewRuntimeStateAtomFamily(stackId), null)
})

/**
 * 设置页面状态
 */
export const setPageStateAtom = atom(null, (get, set, payload: { stackId: string; pageState: PageStateWithValue[] }) => {
    const { stackId, pageState } = payload
    set(pageStateAtomFamily(stackId), pageState)
})

/**
 * 清理当前 stack 的 block runtime value 缓存
 */
export const clearPageStackBlockValuesAtom = atom(null, (get, set, stackId: string) => {
    set(pageStackOfBlockAtom, draft => {
        if (draft?.[stackId]) {
            delete draft[stackId]
        }
    })
})

/**
 * 更新单个查询参数值
 */
export const updateQueryParameterValueAtom = atom(null, (get, set, payload: { stackId: string; queryParamId: string; value: string }) => {
    const { stackId, queryParamId, value } = payload
    set(pageStackAtom, draft => {
        if (!draft) {
            return
        }
        const node = getNode(stackId)(draft)
        if (!node) {
            return
        }
        if (!node.pageQueryParameters) {
            node.pageQueryParameters = {}
        }
        node.pageQueryParameters[queryParamId] = value
    })
})

/**
 * 更新单个页面状态值
 */
export const updatePageStateValueAtom = atom(
    null,
    (get, set, payload: { stackId: string; stateId: string; value: string | number | boolean | string[] | null }) => {
        const { stackId, stateId, value } = payload
        set(pageStateAtomFamily(stackId), draft => {
            const index = draft.findIndex(item => item.id === stateId)
            if (index >= 0) {
                draft[index].value = value
            }
        })
    }
)
